/**
 * Phase B.5 — "Configure LLM keys" pre-flight gate (hook + helper).
 *
 * Fetches the caller's org runtime-config from lexserver and reports
 * whether ANY LLM provider key is configured. Backs the
 * `<ChatLLMKeysGate>` component that mounts above every chat surface
 * (Home global chat + per-case AI chat) so a freshly-provisioned
 * production org doesn't get a generic "agent unreachable" error
 * after typing a message — the gate explains what's missing and
 * deep-links to the runtime-config page in org-admin.
 *
 * Detection logic:
 *   - `GET /api/org/:orgId/runtime-config` returns `data.llm` with
 *     each field either `MASKED_SENTINEL` ("***MASKED***", value
 *     present), `undefined` (not configured), or absent.
 *   - `keyvaultRef://...` values are stored AT REST as encrypted
 *     strings; the API masks them like any other value, so the
 *     "presence-via-mask" check works for both plaintext and KV-ref
 *     storage. Per spec §B.5 step 4 — "Treat keyvaultRef:// values
 *     as PRESENT".
 *   - "Has any key" = any field of `data.llm` is the masked sentinel
 *     (or, defensively, a non-empty string — though the API never
 *     returns plaintext to non-PLATFORM_ADMINs).
 *
 * Polling: 60s `refetchInterval` matches the runtime-config-sync
 * cadence the runtime container uses to pick up newly-configured
 * keys, so the gate dismisses within one cycle of an admin saving
 * a key on `/org/config`.
 */

import { useQuery } from "@tanstack/react-query";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { fetchOrgRuntimeConfig } from "@/lib/lexserver-client";

/** Sentinel string the lexserver returns for present-but-masked secrets. */
const MASKED_SENTINEL = "***MASKED***";

/**
 * Pure helper — given the masked `data.llm` object the API returns,
 * report whether at least one key is configured. Exported for unit
 * testing without mounting React.
 */
export function hasAnyConfiguredLLMKey(
  llm: Record<string, string | undefined | null> | undefined,
): boolean {
  if (!llm) return false;
  for (const value of Object.values(llm)) {
    // Masked sentinel ⇒ present. Any other non-empty string ⇒ also
    // present (PLATFORM_ADMIN unmask path; the gate doesn't care about
    // plaintext, just presence). null / undefined / "" ⇒ absent.
    if (value === MASKED_SENTINEL) return true;
    if (typeof value === "string" && value.length > 0) return true;
  }
  return false;
}

export type OrgLLMKeysStatus = "loading" | "ready" | "error";

export interface UseOrgLLMKeysStatusResult {
  status: OrgLLMKeysStatus;
  /**
   * `true` when at least one LLM key is configured for the caller's
   * org. `null` while loading or on error — consumers should fail
   * OPEN (render chat normally) in those states; the gate only
   * activates on a definitive `false`.
   */
  hasAnyKey: boolean | null;
}

/**
 * Resolve the caller's orgId via `useAuthIdentity` and report whether
 * any LLM key is configured. Polls every 60s so the gate dismisses
 * within one cycle of an admin adding a key on `/org/config`.
 *
 * Fail-open posture: while identity is still loading OR the fetch
 * throws (network blip, 5xx), `hasAnyKey` is `null`. The gate is
 * gated on `hasAnyKey === false` only — never blocks the chat on
 * a transient failure or pre-identity render.
 */
export function useOrgLLMKeysStatus(): UseOrgLLMKeysStatusResult {
  const { identity, status: identityStatus } = useAuthIdentity();
  const orgId = identity?.orgId;

  const query = useQuery<boolean>({
    queryKey: ["org", orgId, "llm-keys-status"],
    queryFn: async () => {
      if (!orgId) throw new Error("orgId not yet resolved");
      const config = await fetchOrgRuntimeConfig(orgId);
      return hasAnyConfiguredLLMKey(config.llm);
    },
    enabled: !!orgId,
    // 60s — matches the runtime-config-sync cadence per plan §B.5.
    refetchInterval: 60_000,
    // Stale after 30s so a manual focus-change refetch picks up
    // newly-saved keys faster than the 60s poll if the user just
    // saved on /org/config and tabbed back.
    staleTime: 30_000,
    // Don't retry on error — fail open and let the next 60s poll
    // recover. Retrying would stall the chat surface waiting for the
    // gate to render a final state.
    retry: false,
  });

  if (identityStatus === "loading" || (!!orgId && query.isLoading)) {
    return { status: "loading", hasAnyKey: null };
  }
  if (identityStatus === "error" || query.isError || !orgId) {
    return { status: "error", hasAnyKey: null };
  }
  return { status: "ready", hasAnyKey: query.data ?? null };
}
